import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import Qs from "qs";
import JwtService from "@/core/services/jwt.service";
import i18nService from "@/core/services/i18n.service";

export const API_URL = process.env.VUE_APP_API + "/api";

/**
 * Service to call HTTP request via Axios
 */
const ApiService = {
  ERRORS_CODE: {
    MAX_ATTEMPT: "max_attempt",
    WRONG_PARAMETERS: "wrong_parameters",
    USER_ALREADY_EXIST: "user_already_exist",
    USER_CREATION_FAILED: "user_creation_failed",
    RECAPTCHA_VERIFY_FAILED: "recaptcha_verify_failed",
    USER_REGISTRATION_DISABLED: "user_registration_disabled",
    AUTHENTICATION: {
      HANDLED_ERRORS: [
        "invalid_authorizations",
        "invalid_claims",
        "invalid_claims_roles",
        "invalid_client",
        "invalid_client_options",
        "invalid_user",
        "invalid_user_session",
        "invalid_parameters",
        "authentication_failed",
        "empty_user_roles",
        "unknown",
      ],
    },
  },

  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = API_URL;
    Vue.axios.defaults.paramsSerializer = (p) => {
      return Qs.stringify(p, { arrayFormat: "brackets" });
    };
  },

  /**
   * Set the default HTTP request headers
   */
  setHeader() {
    Vue.axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${JwtService.getToken()}`;
    Vue.axios.defaults.headers.common[
      "Accept-Language"
    ] = i18nService.getActiveLanguage();
  },

  query(resource, config = {}) {
    return Vue.axios.get(resource, config).catch((error) => {
      // console.log(error);
      throw new Error(`[KT] ApiService ${error}`);
    });
  },

  // TODO : refacto Api.get
  /**
  //  * Send the GET HTTP request
  //  * @param resource
  //  * @param slug
  //  * @returns {*}
  //  */
  // query(resource, slug = "", params={}) {
  //   return Vue.axios.get(resource, params).catch(error => {
  //     // console.log(error);
  //     throw new Error(`[KT] ApiService ${error}`);
  //   });
  // },

  /**
   * Send the GET HTTP request
   * @param resource
   * @param slug
   * @param config
   * @returns {*}
   */
  get(resource, slug = "", config = {}) {
    let url = new URL(resource + (slug ? "/" + slug : ""), API_URL);
    url.searchParams.append("nocache", new Date().getTime());
    return Vue.axios.get(url.pathname + url.search, config).catch((error) => {
      throw new Error(`[KT] ApiService ${error}`);
    });
  },

  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @param config
   * @returns {*}
   */
  post(resource, params, config = {}) {
    return Vue.axios.post(`${resource}`, params, {
      ...config,
      headers: {
        "Accept-Language": "fr_FR",
        ...config.headers,
      },
    });
  },

  authenticate(resource, params) {
    return Vue.axios.post(`${resource}`, params, {
      baseURL: process.env.VUE_APP_API,
    });
  },

  authenticate_sso(resource, params) {
    return Vue.axios.get(`${resource}`, {
      params: params,
      withCredentials: true,
      baseURL: process.env.VUE_APP_API,
    });
  },

  /**
   * Send the UPDATE HTTP request
   * @param resource
   * @param slug
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  update(resource, slug, params) {
    return Vue.axios.put(`${resource}/${slug}`, params);
  },

  /**
   * Send the PUT HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  put(resource, params) {
    return Vue.axios.put(`${resource}`, params);
  },

  /**
   * Send the PATCH HTTP request
   * @param resource
   * @param slug
   * @param params
   * @param config
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  patch(resource, slug, params, config = {}) {
    return Vue.axios.patch(`${resource}/${slug}`, params, {
      ...config,
      headers: {
        "Content-Type": "application/merge-patch+json",
        ...config.headers,
      },
    });
  },

  /**
   * Send the DELETE HTTP request
   * @param resource
   * @returns {*}
   */
  delete(resource) {
    return Vue.axios.delete(resource).catch((error) => {
      // console.log(error);
      throw new Error(`[RWV] ApiService ${error}`);
    });
  },
};

export default ApiService;
